import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { LMListDragDropEvent } from '@list/lm-list.component';

@Injectable()
export class LMListEventService {

  // Observable sources
  private cancelablePagingEventSource = new Subject<LMCancelablePagingEvent>();
  private checkControlEventSource = new Subject<LMCheckControlEvent>();
  private calendarContextMenuEventSource = new Subject<LMContextMenuEvent>();
  private contextMenuEventSource = new Subject<LMContextMenuEvent>();
  private cubeDataLoadedEventSource = new Subject<LMListPagingResponse>();
  private dataLoadedEventSource = new Subject<LMListPagingResponse>();
  private fieldChangedEventSource = new Subject<LMListFieldChangedEvent>();
  private formButtonEventEventSource = new Subject<LMFormButtonEvent>();
  private listDragDropEventSource = new Subject<LMListDragDropEvent>();
  private listKeyDownEventSource = new Subject<LMListKeyDownEvent>();
  private listRowActivatedEventSource = new Subject<LMListRowActivatedEvent>();
  private listRowDeactivatedEventSource = new Subject<LMListRowActivatedEvent>();
  private listRowClickedEventSource = new Subject<LMListRowClickedEvent>();
  private listRowSelectedEventSource = new Subject<LMListRowSelectedEvent>();
  private listRowDoubleClickedEventSource = new Subject<LMListRowClickedEvent>();
  private listGridBeforePagingSource = new Subject<LMListGridPagingEvent>();
  private listGridAfterPagingSource = new Subject<LMListPagingResponse>();
  private listGridContinuePagingSource = new Subject<LMListGridPagingEvent>();
  private listHierarchyDoubleClickedEventSource = new Subject<LMDataView>();
  private hierarchyActionMenuItemSelectedEventSource = new Subject<LMBusActionReference>();
  private listBarCodeClickedEventSource = new Subject<LMListBarCodeClickedEvent>();
  private listLookupClickedEventSource = new Subject<LMListLookupClickedEvent>();
  private listFieldLookupClickedEventSource = new Subject<LMListFieldLookupClickedEvent>();
  private listFilterLookupClickedEventSource = new Subject<LMListFilterLookupClickedEvent>();
  private listFilterClearClickedEventSource = new Subject<LMListFilterClearClickedEvent>();
  private listOpenCompactFormEventSource = new Subject<LMListOpenCompactFormEvent>();
  private uiLinkEventSource = new Subject<LMUILinkEvent>();
  private sendContextMessageEventSource = new Subject<any>();
  private settingsChangedEventSource = new Subject<LMListSettingsChangedEvent>();
  private toolbarButtonEventSource = new Subject<LMToolbarButtonEvent>();
  private buildToolbarEventSource = new Subject();
  private updateToolbarStateEventSource = new Subject();
  private cubeEventSource = new Subject<LMCubeEvent>();
  private clearSearchCriteriaSource = new Subject();
  private busyIndicatorDisplayedSource = new Subject<LMBusyIndicatorDisplayedEvent>();
  private updateFilterCriteriaSource = new Subject();
  private drawerCollapseSource = new Subject<boolean>();
  private filterOperatorChangedSource = new Subject<LMListFilterOperator>();
  // private filterCriteriaFocusedOutSource = new Subject<boolean>();

  // Observable streams
  cancelablePagingEvent$ = this.cancelablePagingEventSource.asObservable();
  checkControlEvent$ = this.checkControlEventSource.asObservable();
  calendarContextMenuEvent$ = this.calendarContextMenuEventSource.asObservable();
  contextMenuEvent$ = this.contextMenuEventSource.asObservable();
  cubeDataLoadedEvent$ = this.cubeDataLoadedEventSource.asObservable();
  dataLoadedEvent$ = this.dataLoadedEventSource.asObservable();
  fieldChangedEvent$ = this.fieldChangedEventSource.asObservable();
  formButtonEvent$ = this.formButtonEventEventSource.asObservable();
  listDragDropEvent$ = this.listDragDropEventSource.asObservable();
  listKeyDownEvent$ = this.listKeyDownEventSource.asObservable();
  listRowActivatedEvent$ = this.listRowActivatedEventSource.asObservable();
  listRowDeactivatedEvent$ = this.listRowDeactivatedEventSource.asObservable();
  listRowSelectedEvent$ = this.listRowSelectedEventSource.asObservable();
  listRowClickedEvent$ = this.listRowClickedEventSource.asObservable();
  listRowDoubleClickedEvent$ = this.listRowDoubleClickedEventSource.asObservable();
  listGridBeforePagingEvent$ = this.listGridBeforePagingSource.asObservable();
  listGridAfterPagingEvent$ = this.listGridAfterPagingSource.asObservable();
  listGridContinuePagingEvent$ = this.listGridContinuePagingSource.asObservable();
  listHierarchyDoubleClickedEvent$ = this.listHierarchyDoubleClickedEventSource.asObservable();
  hierarchyActionMenuItemSelectedEvent$ = this.hierarchyActionMenuItemSelectedEventSource.asObservable();
  listBarCodeClickedEvent$ = this.listBarCodeClickedEventSource.asObservable();
  listLookupClickedEvent$ = this.listLookupClickedEventSource.asObservable();
  listFieldLookupClickedEvent$ = this.listFieldLookupClickedEventSource.asObservable();
  listFilterLookupClickedEvent$ = this.listFilterLookupClickedEventSource.asObservable();
  listFilterClearClickedEvent$ = this.listFilterClearClickedEventSource.asObservable();
  listOpenCompactFormEvent$ = this.listOpenCompactFormEventSource.asObservable();
  uiLinkEvent$ = this.uiLinkEventSource.asObservable();
  sendContextMessageEvent$ = this.sendContextMessageEventSource.asObservable();
  settingsChangedEvent$ = this.settingsChangedEventSource.asObservable();
  toolbarButtonEvent$ = this.toolbarButtonEventSource.asObservable();
  buildToolbarEvent$ = this.buildToolbarEventSource.asObservable();
  updateToolbarStateEvent$ = this.updateToolbarStateEventSource.asObservable();
  cubeEvent$ = this.cubeEventSource.asObservable();
  clearSearchCriteriaEvent$ = this.clearSearchCriteriaSource.asObservable();
  busyIndicatorDisplayedEvent$ = this.busyIndicatorDisplayedSource.asObservable();
  updateFilterCriteriaSource$ = this.updateFilterCriteriaSource.asObservable();
  drawerCollapse$ = this.drawerCollapseSource.asObservable();
  filterOperatorChangedEvent$ = this.filterOperatorChangedSource.asObservable();
  // filterCriteriaFocusedOutSource$ = this.filterCriteriaFocusedOutSource.asObservable();

  // Service message commands
  cancelablePagingEvent(event: LMCancelablePagingEvent) {
    this.cancelablePagingEventSource.next(event);
  }

  checkControlEvent(event: LMCheckControlEvent) {
    this.checkControlEventSource.next(event);
  }

  calendarContextMenuEvent(event: LMContextMenuEvent) {
    this.calendarContextMenuEventSource.next(event);
  }

   contextMenuEvent(event: LMContextMenuEvent) {
    this.contextMenuEventSource.next(event);
  }

  cubeDataLoadedEvent(event: LMListPagingResponse) {
    this.cubeDataLoadedEventSource.next(event);
  }

  dataLoadedEvent(event: LMListPagingResponse) {
    this.dataLoadedEventSource.next(event);
  }

  fieldChangedEvent(event: LMListFieldChangedEvent) {
    this.fieldChangedEventSource.next(event);
  }

  formButtonEvent(event: LMFormButtonEvent) {
    this.formButtonEventEventSource.next(event);
  }

  listDragDropEvent(event: LMListDragDropEvent) {
    this.listDragDropEventSource.next(event);
  }

  listKeyDownEvent(event: LMListKeyDownEvent) {
    this.listKeyDownEventSource.next(event);
  }

  listRowActivatedEvent(event: LMListRowActivatedEvent) {
    this.listRowActivatedEventSource.next(event);
  }

  listRowDeactivatedEvent(event: LMListRowActivatedEvent) {
    this.listRowDeactivatedEventSource.next(event);
  }

  listRowClickedEvent(event: LMListRowClickedEvent) {
    this.listRowClickedEventSource.next(event);
  }

  listRowSelectedEvent(event: LMListRowSelectedEvent) {
    this.listRowSelectedEventSource.next(event);
  }

  listRowDoubleClickedEvent(event: LMListRowClickedEvent) {
    this.listRowDoubleClickedEventSource.next(event);
  }

  listGridBeforePagingEvent(event: LMListGridPagingEvent) {
    this.listGridBeforePagingSource.next(event);
  }

  listGridAfterPagingEvent(event: LMListPagingResponse) {
    this.listGridAfterPagingSource.next(event);
  }

  listGridContinuePagingEvent(event: LMListGridPagingEvent) {
    this.listGridContinuePagingSource.next(event);
  }

  listHierarchyDoubleClickedEvent(dataView: LMDataView) {
    this.listHierarchyDoubleClickedEventSource.next(dataView);
  }

  hierarchyActionMenuItemSelectedEvent(event: LMBusActionReference) {
    this.hierarchyActionMenuItemSelectedEventSource.next(event);
  }

  listBarCodeClickedEvent(event: LMListBarCodeClickedEvent) {
    this.listBarCodeClickedEventSource.next(event);
  }

  listLookupClickedEvent(event: LMListLookupClickedEvent) {
    this.listLookupClickedEventSource.next(event);
  }

  listFieldLookupClickedEvent(event: LMListFieldLookupClickedEvent) {
    this.listFieldLookupClickedEventSource.next(event);
  }

  listFilterLookupClickedEvent(event: LMListFilterLookupClickedEvent) {
    this.listFilterLookupClickedEventSource.next(event);
  }

  listFilterClearClickedEvent(event: LMListFilterClearClickedEvent) {
    this.listFilterClearClickedEventSource.next(event);
  }

  listOpenCompactFormEvent(event: LMListOpenCompactFormEvent) {
    this.listOpenCompactFormEventSource.next(event);
  }

  uiLinkEvent(event: LMUILinkEvent) {
    this.uiLinkEventSource.next(event);
  }

  clearSearchCriteriaEvent() {
    this.clearSearchCriteriaSource.next();
  }

  sendContextMessageEvent(event: any) {
    this.sendContextMessageEventSource.next(event);
  }

  settingsChangedEvent(event: LMListSettingsChangedEvent) {
    this.settingsChangedEventSource.next(event);
  }

  toolbarButtonEvent(event: LMToolbarButtonEvent) {
    this.toolbarButtonEventSource.next(event);
  }

  buildToolbarEvent() {
    this.buildToolbarEventSource.next();
  }

  updateToolbarStateEvent() {
    this.updateToolbarStateEventSource.next();
  }

  cubeEvent(event: LMCubeEvent) {
    this.cubeEventSource.next(event);
  }

  busyIndicatorDisplayedEvent(event: LMBusyIndicatorDisplayedEvent) {
    this.busyIndicatorDisplayedSource.next(event);
  }

  updateFilterCriteriaEvent(filterCriteria: LMFilterFieldCriteria[]) {
    this.updateFilterCriteriaSource.next(filterCriteria);
  }

  expandDrawerSide(): void {
    this.drawerCollapseSource.next(false);
  }

  collapseDrawerSide(): void {
    this.drawerCollapseSource.next(true);
  }

  filterOperatorChangedEvent(event: LMListFilterOperator): void {
    this.filterOperatorChangedSource.next(event);
  }

  // filterCriteriaFocusedOutEvent(changed: boolean): void {
  //   this.filterCriteriaFocusedOutSource.next(changed);
  // }
}
